/**
 * Helius Sender Client
 *
 * Routes transactions through Helius Sender endpoint for MEV protection
 * and improved landing rates via dual routing to staked validators + Jito.
 *
 * Sender endpoint: https://sender.helius-rpc.com/fast
 * - Free on all Helius plans (no credits consumed)
 * - HTTPS auto-routes to nearest global region
 * - Dual-routes to both staked connections and Jito
 *
 * Usage:
 * 1. Call prepareTransactionForSender() to add compute budget + Jito tip BEFORE signing
 * 2. Sign the transaction via Turnkey
 * 3. Call sendViaSender() to broadcast via the Sender endpoint
 */

import {
  PublicKey,
  Transaction,
  SystemProgram,
  ComputeBudgetProgram,
} from '@solana/web3.js';
import { getPriorityFee, type PriorityFeeUrgency } from '@/lib/solana/priorityFee';

// Debug logging
const DEBUG = import.meta.env.DEV;

// ============================================================================
// Jito Tip Accounts (mainnet)
// ============================================================================

const TIP_ACCOUNTS = [
  '4ACfpUFoaSD9bfPdeu6DBt89gB6ENTeHBXCAi87NhDEE',
  'D2L6yPZ2FmmmTKPgzaMKdhu6EWZcTpLy1Vhx8uvZe7NZ',
  '9bnz4RShgq1hAnLnZbP8kbgBg1kEmcJBYQq3gQbmnSta',
  '5VY91ws6B2hMmBFRsXkoAAdsPHBJwRfBht4DXox3xkwn',
  '2nyhqdwKcJZR2vcqCyrYsaPVdAnFoJjiksCXJ7hfEYgD',
  '2q5pghRs6arqVjRvT5gfgWfWcHWmw1ZuCzphgd5KfWGJ',
  'wyvPkWjVZz1M8fHQnMMCDTQDbkManefNNhweYk5WkcF',
  '3KCKozbAaF75qEU33jtzozcJ29yJuaLJTy2jFdzUY8bT',
  '4vieeGHPYPG2MmyPRcYjdiDmmhN3ww7hsFNap8pVN3Ey',
  '4TQLFNWK8AovT1gFvda5jfw2oJeRMKEmw7aH6MGBJ3or',
];

// ============================================================================
// Configuration
// ============================================================================

// Static fallback tip amounts in lamports per priority level. Represent
// the floor: the dynamic getPriorityFee() lookup picks max(fallback,
// live percentile) so labels like "Normal: 0.0002 SOL" remain a truthful
// minimum even when actual congestion drives the tip higher. Helius
// minimum for dual routing is 200,000 lamports.
export const FALLBACK_TIP_AMOUNTS = {
  normal: 200_000,   // 0.0002 SOL
  fast: 300_000,     // 0.0003 SOL
  turbo: 500_000,    // 0.0005 SOL
} as const;

export type PriorityLevel = keyof typeof FALLBACK_TIP_AMOUNTS;

// Compute budget defaults for Meteora DBC swaps. CU LIMIT is sizing
// (not priority), stays static. CU PRICE becomes a fallback floor for
// the dynamic helper.
const DEFAULT_COMPUTE_UNIT_LIMIT = 150_000;
export const FALLBACK_COMPUTE_UNIT_PRICE = 200_000; // microLamports

const PRIORITY_LEVEL_TO_URGENCY: Record<PriorityLevel, PriorityFeeUrgency> = {
  normal: 'low',
  fast: 'medium',
  turbo: 'high',
};

// Helius Sender endpoint (HTTPS for browser, auto-routes geographically)
const SENDER_ENDPOINT = 'https://sender.helius-rpc.com/fast';

// ============================================================================
// Transaction Preparation
// ============================================================================

/**
 * Get a random Jito tip account for MEV protection.
 * Randomizing across accounts distributes load.
 */
export function getRandomTipAccount(): PublicKey {
  const idx = Math.floor(Math.random() * TIP_ACCOUNTS.length);
  return new PublicKey(TIP_ACCOUNTS[idx]);
}

/**
 * Prepare a transaction for Helius Sender by adding compute budget
 * and Jito tip instructions.
 *
 * MUST be called BEFORE signing the transaction.
 *
 * Instruction order after preparation:
 *   [ComputeUnitLimit, ComputeUnitPrice, ...originalInstructions, JitoTip]
 *
 * @param transaction - The unsigned transaction to prepare
 * @param feePayer - The wallet paying fees and the Jito tip
 * @param options - Priority level and optional compute budget overrides
 * @returns The same transaction (mutated in place) with added instructions
 */
export async function prepareTransactionForSender(
  transaction: Transaction,
  feePayer: PublicKey,
  options?: {
    priorityLevel?: PriorityLevel;
    computeUnitLimit?: number;
    computeUnitPrice?: number;
  }
): Promise<Transaction> {
  const priorityLevel = options?.priorityLevel ?? 'normal';
  const cuLimit = options?.computeUnitLimit ?? DEFAULT_COMPUTE_UNIT_LIMIT;

  // Caller-provided overrides take precedence (used by tests + advanced
  // flows). Otherwise call the dynamic helper which falls back to the
  // FALLBACK_ floor on any failure.
  let tipLamports: number;
  let cuPrice: number;
  let feeSource: 'rpc' | 'fallback' | 'override' = 'override';
  if (options?.computeUnitPrice !== undefined) {
    cuPrice = options.computeUnitPrice;
    tipLamports = FALLBACK_TIP_AMOUNTS[priorityLevel];
  } else {
    const dynamic = await getPriorityFee(PRIORITY_LEVEL_TO_URGENCY[priorityLevel]);
    cuPrice = dynamic.cuPriceMicroLamports;
    tipLamports = dynamic.tipLamports;
    feeSource = dynamic.source;
  }

  const tipAccount = getRandomTipAccount();

  // Compute budget instructions (must be first in transaction)
  const computeInstructions = [
    ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: cuPrice }),
  ];

  // Jito tip instruction (must be last in transaction)
  const tipInstruction = SystemProgram.transfer({
    fromPubkey: feePayer,
    toPubkey: tipAccount,
    lamports: tipLamports,
  });

  // Rebuild instruction order: [CU limit, CU price, ...original, tip]
  transaction.instructions = [
    ...computeInstructions,
    ...transaction.instructions,
    tipInstruction,
  ];

  DEBUG && console.log('[Sender] Transaction prepared for Helius Sender:', {
    computeUnitLimit: cuLimit,
    computeUnitPrice: cuPrice,
    tipLamports,
    tipAccount: tipAccount.toBase58().slice(0, 8) + '...',
    priorityLevel,
    feeSource,
    totalInstructions: transaction.instructions.length,
  });

  return transaction;
}

// ============================================================================
// Transaction Broadcasting
// ============================================================================

/**
 * Send a serialized transaction via Helius Sender endpoint.
 *
 * The Sender endpoint dual-routes to both staked validators and Jito
 * for maximum landing probability and speed.
 *
 * Per Helius docs:
 * - skipPreflight: true (speed optimized)
 * - maxRetries: 0 (client handles retries)
 * - encoding: base64
 *
 * @param serializedTxBase64 - Base64-encoded signed transaction
 * @returns Transaction signature string
 * @throws Error if Sender returns an error
 */
export async function sendViaSender(
  serializedTxBase64: string,
  preExtractedSignature?: string,
): Promise<string> {
  DEBUG && console.log('[Sender] Broadcasting via Helius Sender:', SENDER_ENDPOINT);

  let response: Response;
  try {
    response = await fetch(SENDER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: Date.now().toString(),
        method: 'sendTransaction',
        params: [
          serializedTxBase64,
          {
            encoding: 'base64',
            skipPreflight: true,
            maxRetries: 0,
          },
        ],
      }),
    });
  } catch (networkErr) {
    // Network-level failure (DNS, TLS, connection refused). Same fallback
    // as HTTP-error path below: if we have a pre-extracted signature, return
    // it so the caller can poll confirmation against the chain. Jito dual
    // routing inside Sender may have already delivered the tx in some cases.
    if (preExtractedSignature) {
      console.warn(
        '[Sender] Network failure to Sender, returning pre-extracted signature:',
        networkErr instanceof Error ? networkErr.message : String(networkErr),
      );
      return preExtractedSignature;
    }
    throw networkErr;
  }

  if (!response.ok) {
    // Helius Sender returns 500 / 502 / 503 transiently during their own
    // infrastructure incidents. The signed tx is fully formed and may still
    // land via Sender's Jito dual-routing path even when the JSON-RPC
    // response failed. Per project memory "Sender 500 handling": return the
    // pre-extracted signature so the caller can poll confirmation; if the
    // tx didn't land, polling will time out and surface a real failure.
    if (preExtractedSignature) {
      console.warn(
        `[Sender] HTTP ${response.status} ${response.statusText}, returning pre-extracted signature (Jito dual-routing may still deliver)`,
      );
      return preExtractedSignature;
    }
    throw new Error(`Sender HTTP error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  if (result.error) {
    const errMsg = result.error.message || JSON.stringify(result.error);
    DEBUG && console.error('[Sender] Sender error:', result.error);
    // JSON-RPC error path: same fallback. The tx is signed and broadcast-
    // attempted; if it didn't land, confirmation polling will surface the
    // failure with the chain as source of truth.
    if (preExtractedSignature) {
      console.warn(
        `[Sender] JSON-RPC error "${errMsg}", returning pre-extracted signature`,
      );
      return preExtractedSignature;
    }
    throw new Error(`Sender error: ${errMsg}`);
  }

  const signature = result.result;
  DEBUG && console.log('[Sender] Broadcast successful, signature:', signature);
  return signature;
}

// ============================================================================
// Exports
// ============================================================================

export { TIP_ACCOUNTS };
